
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Paper,
  Chip,
  Divider,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";

import BreadcrumbsBar from "../../../component/common/BreadcrumbsBar";
import PageHeader from "../../../component/common/PageHeader";

import ReportCategory from "./ReportCategory";
import { reportGroups } from "../General/ReportsGroup";

import { procReportProcess } from "../../../services/reportService";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function MonthlyReport() {
  const navigate = useNavigate();

  const currentDate = new Date();

  const [selectedMonth, setSelectedMonth] = useState(
    () =>
      Number(sessionStorage.getItem("selectedMonth")) ||
      currentDate.getMonth() + 1
  );

  const [selectedYear, setSelectedYear] = useState(
    () =>
      Number(sessionStorage.getItem("selectedYear")) ||
      currentDate.getFullYear()
  );

  const [month, setMonth] = useState(selectedMonth);
  const [year, setYear] = useState(selectedYear);

  const [showReports, setShowReports] = useState(() => {
    const m = sessionStorage.getItem("selectedMonth");
    const y = sessionStorage.getItem("selectedYear");

    return !!(m && y);
  });

  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    try {
      setLoading(true);

      const res = await procReportProcess(
        selectedMonth,
        selectedYear
      );

      alert(res.data.message);

      setMonth(selectedMonth);
      setYear(selectedYear);

      sessionStorage.setItem(
        "selectedMonth",
        selectedMonth
      );

      sessionStorage.setItem(
        "selectedYear",
        selectedYear
      );

      setShowReports(true);
    } catch (err) {
      setShowReports(false);

      alert(
        err.response?.data?.message ||
          "Failed to generate report."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleView = (report) => {
    navigate(report.route, {
      state: {
        ...report.state,
        month,
        year,
      },
    });
  };

  const selectedMonthName = months[selectedMonth - 1];

  return (
    <Box>
      <BreadcrumbsBar page="Monthly Reports" />

      {/* Professional Page Header */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 3,
          p: {
            xs: 2.5,
            md: 3.5,
          },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background */}
        <Box
          sx={{
            position: "absolute",
            right: -40,
            top: -50,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
          }}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2.5}
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #1976d2, #42a5f5)",
              color: "white",
              boxShadow:
                "0 8px 20px rgba(25,118,210,0.25)",
              flexShrink: 0,
            }}
          >
            <AssessmentIcon sx={{ fontSize: 34 }} />
          </Box>

          {/* Title */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                fontSize: {
                  xs: "1.65rem",
                  md: "2rem",
                },
              }}
            >
              Monthly Reports Center
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 650,
              }}
            >
              Generate, view and download monthly
              reports for your selected accounting period.
            </Typography>
          </Box>

          {/* Current Period */}
          <Box
            sx={{
              minWidth: {
                xs: "100%",
                sm: 190,
              },
              textAlign: {
                xs: "left",
                sm: "right",
              },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              Selected Period
            </Typography>

            <Typography
              variant="h6"
              fontWeight={700}
              color="primary"
              sx={{ mt: 0.3 }}
            >
              {selectedMonthName} {selectedYear}
            </Typography>

            {showReports && (
              <Chip
                label="Reports Ready"
                size="small"
                sx={{
                  mt: 0.7,
                  fontWeight: 600,
                }}
                color="success"
                variant="outlined"
              />
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Report Period Selector */}
      <Paper
        elevation={0}
        className="no-print"
        sx={{
          mb: 4,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <CalendarMonthIcon
              color="primary"
              sx={{ fontSize: 27 }}
            />

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                Report Period
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Select the month and year for which
                reports should be generated.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Controls */}
        <Box
          sx={{
            p: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
          >
            {/* Month */}
            <TextField
              select
              fullWidth
              label="Month"
              value={selectedMonth}
              onChange={(e) =>
                setSelectedMonth(Number(e.target.value))
              }
              sx={{
                minWidth: {
                  sm: 220,
                },
              }}
            >
              {months.map((m, index) => (
                <MenuItem
                  key={index}
                  value={index + 1}
                >
                  {m}
                </MenuItem>
              ))}
            </TextField>

            {/* Year */}
            <TextField
              label="Year"
              type="number"
              value={selectedYear}
              onChange={(e) =>
                setSelectedYear(Number(e.target.value))
              }
              inputProps={{
                min: 2000,
                max: 2100,
              }}
              sx={{
                width: {
                  xs: "100%",
                  sm: 150,
                },
              }}
            />

            {/* Generate */}
            <Button
              variant="contained"
              size="large"
              startIcon={
                <SearchIcon />
              }
              onClick={loadReport}
              disabled={loading}
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 180,
                },
                height: 56,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
                boxShadow:
                  "0 5px 14px rgba(25,118,210,0.25)",
              }}
            >
              {loading
                ? "Generating..."
                : "Generate Reports"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Reports */}
      {showReports && (
        <Box>
          {/* Reports Section Header */}
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Available Reports
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Reports generated for{" "}
                <strong>
                  {selectedMonthName} {selectedYear}
                </strong>
              </Typography>
            </Box>

            <Chip
              icon={<AssessmentIcon />}
              label={`${reportGroups.reduce(
                (total, group) =>
                  total + group.reports.length,
                0
              )} Reports`}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 600,
              }}
            />
          </Stack>

          <Divider sx={{ mb: 2.5 }} />

          {/* Report Categories */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(350px, 1fr))",
              gap: 2,
            }}
          >
            {reportGroups.map((group) => (
              <ReportCategory
                key={group.title}
                title={group.title}
                icon={group.icon}
                chipColor={group.chipColor}
                reports={group.reports}
                onView={handleView}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

